/* eslint-disable */
import { stringify } from 'querystring';
import { getIn } from 'seamless-immutable';
import uniqBy from 'lodash/uniqBy';
import { formatPrice } from '@catalogo/core-helpers/price';
import { getImage } from '@catalogo/service-americanas-common/src/helpers/image';
import { getCartLinesMap } from '@catalogo/service-americanas-cart/src/helpers/add-to-cart';
import configuration from './config.json';

const fmtServiceOption = ({ maxInstallment, id, selectedServices, name, sku }) => option => ({
  periodText: option.type === 'DEFAULT' ? `Com ${name}` : configuration.periodDisplay[option.type],
  installmentValue: option.price / maxInstallment,
  installmentText: `R$ ${formatPrice(option.price / maxInstallment)}`,
  installmentQty: maxInstallment,
  price: option.price,
  priceText: `R$ ${formatPrice(option.price)}`,
  serviceId: id,
  optionId: option.type,
  productSku: sku.id,
  sku: option.sku,
  benefits: true,
  selected: selectedServices[`${sku.id}:${id}`] === option.type,
});

const fmtServiceOptions = (maxInstallment, selectedServices, sku, hasManySkus) => service => {
  const { id, name } = service;
  const serviceConfig = configuration.services[id] || configuration.services['DEFAULT'];
  const { benefits } = serviceConfig;
  const selectDefault = !selectedServices[`${sku.id}:${id}`] || selectedServices[`${sku.id}:${id}`] === 'NONE';
  const defaultOption = [
    {
      periodText: `Sem ${name}`,
      productSku: sku.id,
      optionId: 'NONE',
      serviceId: id,
      benefits: false,
      selected: selectDefault,
    },
  ];
  const options = service.options.map(fmtServiceOption({ maxInstallment, id, selectedServices, name, sku }));
  const serviceOptions = defaultOption.concat(options);
  const { modalId } = serviceConfig;

  const hasActiveBenefits = !selectDefault;

  let serviceInfo = null;
  if (hasManySkus) {
    serviceInfo = {
      label: reduceString(sku.name),
      message: `Serviço disponível para a <strong>${sku.name}</strong>.`,
    };
  }

  return { options: serviceOptions, id, benefits, name, hasActiveBenefits, modalId, serviceInfo };
};

export const reduceString = string => {
  const firstWhiteSpace = string.indexOf(' ');
  return string.slice(0, firstWhiteSpace);
};

export const setServicePriority = service => {
  if (service.id.includes('ROUBO_FURTO')) {
    return -2;
  }

  if (service.id.includes('GARANTIA_ESTENDIDA')) {
    return -1;
  }

  return 0;
};

export const fmtServices = ({ currentSku, selectedServices = {}, maxInstallment, productId }) => {
  const offer = getIn(currentSku, ['offers', 'result', '0'], {});
  const skus = getIn(offer, ['productServices'], []) || [];
  const hasManySkus = skus.length > 1;

  return skus.map(({ sku, services }) => {
    const formatted = services
      .map(fmtServiceOptions(maxInstallment, selectedServices, sku, hasManySkus));

    return { sku: { ...sku, image: getIn(sku, ['images', '0', 'big']), productId }, services: formatted };
  });
};

export const fmtSummary = (skus, maxInstallment) => {
  const services = skus.map(({ services }) => services).flat();

  const selectedServices = services.map(({ options, name, id, serviceInfo }) => {
    const serviceConfig = configuration.services[id] || configuration.services['DEFAULT'];
    const { productSku, optionId, serviceId, priceText, periodText, price, installmentValue } = options.find(
      ({ selected }) => selected
    );
    const text = ['NONE', 'DEFAULT'].includes(optionId) ? periodText : `${periodText} de ${name}`;
    const term = optionId !== 'NONE' ? serviceConfig.terms : undefined;

    return {
      text,
      priceText,
      newText: serviceInfo ? `${text} para ${serviceInfo?.label}` : text,
      price,
      installmentValue,
      term,
      name,
      active: optionId !== 'NONE',
      key: `${productSku}:${serviceId}`,
    };
  });

  const inCashPrice = selectedServices.reduce((acc, { price }) => (price ? acc + price : acc), 0);
  const inCashText = inCashPrice ? `R$ ${formatPrice(inCashPrice)}` : undefined;
  const installmentPrice = selectedServices.reduce(
    (acc, { installmentValue }) => (installmentValue ? installmentValue + acc : acc),
    0
  );
  const installmentText = installmentPrice ? `${maxInstallment}x de R$ ${formatPrice(installmentPrice)}` : undefined;
  const terms = selectedServices.map(({ term, name }) => ({ term, name })).filter(({ term }) => term);
  const hasSelectedService = selectedServices.some(({ active }) => active);

  return { selectedServices, inCashText, installmentText, terms: uniqBy(terms, 'term'), hasSelectedService };
};

const filterSelectedOption = ({ selected, optionId }) => selected && optionId !== 'NONE';

export const getSelectedServices = skus => {
  const services = skus.map(({ services }) => services).flat();

  return services
    .map(({ options }) => {
      const selectedService = options.find(filterSelectedOption);

      if (!selectedService) {
        return undefined;
      }

      const { serviceId: id, optionId, productSku } = selectedService;
      return { id, optionId, productSku };
    })
    .filter(Boolean);
};

export const getExtraLine = params => {
  const { extra } = params;

  if (!extra || extra === '[{}]') {
    return [];
  }

  try {
    return JSON.parse(extra);
  } catch {
    return [];
  }
};

export const fmtCartBreadcrumb = breadcrumb => {
  const ordered = [...breadcrumb].reverse();
  const serialized = ordered.map(item => `${item.id}:${item.name}`);

  return serialized.join('|');
};

export const fmtCart = ({ params, services, breadcrumb, promocionalTestAb, servicesPDP }) => {
  const cartLine = {
    sku: params.productSku,
    storeId: params.sellerId,
    sellerStoreId: params.sellerStoreId,
    condition: params.condition,
    offerId: params.offerId,
    quantity: params.productQty,
    additionalInfo: {
      breadcrumb: fmtCartBreadcrumb(breadcrumb),
      buyboxField: params.buyboxField,
      buyboxToken: params.buyboxToken,
      pricingId: params.pricingId,
      ["promocional-test-ab"]: promocionalTestAb,
    },
    services: servicesPDP ? [servicesPDP] : getSelectedServices(services),
  };

  const extraLine = getExtraLine(params);

  return [cartLine].concat(extraLine);
};

export const fmtFillerUrl = ({ cart, config, cookies }) => {
  const b2wChannel = getIn(cookies, ['b2wChannel']);
  const filler = getIn(config, ['endpoints', 'filler']);
  const data = JSON.stringify({ products: cart });
  const query = { data };

  if (b2wChannel === 'QLAS') {
    query.c_salesSolution = b2wChannel;
  }

  return `${filler}?${stringify(query)}`;
};

export const fmtTexts = (services, selectedServices, summary) => {
  const filterSelectedServices = Object.values(selectedServices).filter(value => value !== 'NONE');

  const textWithService =
    filterSelectedServices.length > 1 ? 'contratar serviços e ir para a cesta' : 'contratar serviço e ir para a cesta';
  const ctaText = filterSelectedServices.length === 0 ? 'ir para cesta' : textWithService;
  const ctaDisclaimer = fmtDisclaimer(services, filterSelectedServices, summary);
  const sectionTitle = fmtSectionTitle(summary);
  const sectionSubtitleWord = (services?.[0]?.services ?? []).length > 1 ? 'serviços' : 'serviço';

  return {
    ctaText,
    ctaDisclaimer,
    sectionTitle,
    sectionSubtitle: `A contratação de ${sectionSubtitleWord} é opcional.`,
  };
};

const fmtDisclaimer = (services, filterSelectedServices, summary) => {
  const hasMultiplesServices = services?.[0]?.services?.length > 1;
  const hasNoServicesSelected = filterSelectedServices.length === 0;
  const pluralText = 'Sem contratar serviços opcionais';
  const withoutServices = hasMultiplesServices && hasNoServicesSelected ? pluralText : 'Sem contratar serviço opcional';
  const withServicesWord = filterSelectedServices.length > 1 ? 'serviços' : 'serviço';
  const withServices = `Ao clicar em contratar ${withServicesWord} e ir para cesta você aceita os termos e condições: `;

  const text = hasNoServicesSelected ? withoutServices : withServices;

  return text;
};

export const fmtSectionTitle = summary => {
  const services = summary?.selectedServices.map(i => i?.key);
  let text = 'Agora que você já fez sua escolha, complete seu pedido com nossos serviços.';

  const hasOnlyWarranty = services.length === 1 && services.filter(s => s.includes('GARANTIA_ESTENDIDA')).length === 1;
  const hasOnlyFurnitureAssembly =
    services.length === 1 && services.filter(s => s.includes('MONTAGEM_MOVEL')).length === 1;
  if (hasOnlyWarranty) {
    text = 'Agora que você já fez sua escolha, complete seu pedido com nosso serviço de garantia estendida.';
  }

  if (hasOnlyFurnitureAssembly) {
    text = 'Agora que você já fez sua escolha, complete seu pedido com nosso serviço de montagem de móveis.';
  }

  return text;
};

export const openWarrantyInfoModal = history => id => () => {
  const pathname = getIn(history, ['location', 'pathname']);
  const search = getIn(history, ['location', 'search']);

  return history.replace({ pathname, search, hash: '#modal-warranty-info' }, { id });
};

export const fmtSkuInfo = offer => ({
  name: offer?.productServices?.[0]?.sku?.name ?? '',
  image: getImage(offer?.productServices?.[0]?.sku?.images?.[0], 'big'),
});

export const fmtOfferLine = ({ offers, services, sellerStoreId, cartLinesMap }) =>
  offers?.map(offer => {
    const line = cartLinesMap?.get(offer?.offerId);
    const servicesToAdd = !line && services?.length > 0 && offer?.isMain ? services : [];
    return { offerId: offer?.offerId, quantity: offer?.quantity ?? 1 , services: servicesToAdd, sellerStoreId };
  });

export const fmtOffersToAddInCart = ({ params, services, cartLines }) => {
  const extraLine = getExtraLine(params);
  const offers = [{
    productSku: params.productSku,
    offerId: params.offerId,
    quantity: parseInt(params.productQty),
    sellerStoreId: params.sellerStoreId,
    isMain: true,
  }].concat(extraLine);

  const cartLinesMap = getCartLinesMap(cartLines);

  const offersToAdd = offers?.filter((offer) => {
    const isOfferInCart = !!cartLinesMap.get(offer?.offerId);
    return !isOfferInCart;
  });

  return {
    offersToAdd: fmtOfferLine({ offers: offersToAdd, services, sellerStoreId: params.sellerStoreId, cartLinesMap }),    
  };
}

export const fmtServicesToAddInCart = ({ offersToAdd, services, cartLines }) => {
  const hasOfferWithServices = offersToAdd?.some(offer => offer?.services.length > 0);
  
  if (hasOfferWithServices) {
    return { services: [] };
  }

  const line = cartLines?.find(line => line?.product?.sku === services?.[0]?.productSku);
  return { lineId: line?.id, services };
};
